import type { FaqEntry } from "./seo.ts";

// Static copy for the landing page, kept separate from the components so it can
// be unit-tested without a DOM render harness (matches the app's test style).
//
// Positioning leads with the agent-wallet + mobile-approval story (the moat per
// CLAUDE.md: the SDK + the agent + the approval loop), with gaming as the second
// market. No "card networks leave behind" framing — that reads as a high-risk
// signal to payment underwriters.

/** The app (web wallet) lives on `app.ggcoin.ai` once marketing owns the apex/www. */
export const WALLET_URL = "https://app.ggcoin.ai";

/** Developer docs host. */
export const DOCS_URL = "https://docs.ggcoin.ai";

export interface Pillar {
  tag: string;
  title: string;
  body: string;
}

export const PILLARS: readonly Pillar[] = [
  {
    tag: "Agents",
    title: "A wallet built for AI agents",
    body: "Every agent gets its own spend-capped sub-wallet over MCP and can buy what it needs from any platform that runs on GG-Coin — the first money rail built for software that spends on its own.",
  },
  {
    tag: "Approvals",
    title: "You approve every payment",
    body: "Like 3D Secure, but for agents: when an agent goes to pay, you get a push on your phone and confirm with one tap. Nothing moves without you — strong authentication and near-zero chargebacks.",
  },
  {
    tag: "Gaming",
    title: "Game and creator economies",
    body: "Games and creator platforms get a real wallet, an SDK, and a payout-free coin economy in an afternoon — one integration, any country.",
  },
];

export const SDKS = ["JavaScript", "Lua", "Java", "Python", "C#", "PHP", "Go"] as const;
export type Sdk = (typeof SDKS)[number];

// One idiomatic snippet per SDK, all showing the SAME closed-loop flow: init the
// client from an API key, then post a sub-wallet charge (above the owner's limit
// it pauses for a one-tap phone approval). Keyed by SDKS so the showcase can
// switch languages; no payout/redeem — the coin only flows in.
export const SNIPPETS: Record<Sdk, string> = {
  JavaScript: `import { GGCoin } from "@developerz.ai/ggcoin-sdk";

const gg = new GGCoin({ apiKey: process.env.GGCOIN_KEY });

// An agent buys API credits from another platform. Above the
// owner's limit, the charge waits for a one-tap phone approval.
await gg.transactions.post({
  subWallet: agent.walletId,
  amount: 500n,
  memo: "platform-y:api-credits",
});`,
  Lua: `local ggcoin = require("ggcoin")

local gg = ggcoin.new(os.getenv("GGCOIN_KEY"))

-- An agent buys API credits from another platform. Above the
-- owner's limit, the charge waits for a one-tap phone approval.
gg.transactions.post({
  sub_wallet = agent.wallet_id,
  amount = 500,
  memo = "platform-y:api-credits",
})`,
  Java: `import ai.ggcoin.GGCoin;
import ai.ggcoin.Transaction;

var gg = new GGCoin(System.getenv("GGCOIN_KEY"));

// An agent buys API credits from another platform. Above the
// owner's limit, the charge waits for a one-tap phone approval.
gg.transactions().post(Transaction.builder()
    .subWallet(agent.walletId())
    .amount(500)
    .memo("platform-y:api-credits")
    .build());`,
  Python: `import os
from ggcoin import GGCoin

gg = GGCoin(api_key=os.environ["GGCOIN_KEY"])

# An agent buys API credits from another platform. Above the
# owner's limit, the charge waits for a one-tap phone approval.
gg.transactions.post(
    sub_wallet=agent.wallet_id,
    amount=500,
    memo="platform-y:api-credits",
)`,
  "C#": `using GGCoin;

var gg = new GGCoinClient(Environment.GetEnvironmentVariable("GGCOIN_KEY"));

// An agent buys API credits from another platform. Above the
// owner's limit, the charge waits for a one-tap phone approval.
await gg.Transactions.PostAsync(new Transaction {
    SubWallet = agent.WalletId,
    Amount = 500,
    Memo = "platform-y:api-credits",
});`,
  PHP: `<?php
use GGCoin\\GGCoin;

$gg = new GGCoin(getenv('GGCOIN_KEY'));

// An agent buys API credits from another platform. Above the
// owner's limit, the charge waits for a one-tap phone approval.
$gg->transactions->post([
    'subWallet' => $agent->walletId,
    'amount'    => 500,
    'memo'      => 'platform-y:api-credits',
]);`,
  Go: `import "github.com/ggcoin/ggcoin-go"

gg := ggcoin.New(os.Getenv("GGCOIN_KEY"))

// An agent buys API credits from another platform. Above the
// owner's limit, the charge waits for a one-tap phone approval.
gg.Transactions.Post(ctx, ggcoin.Transaction{
    SubWallet: agent.WalletID,
    Amount:    500,
    Memo:      "platform-y:api-credits",
})`,
};

// ---------------------------------------------------------------------------
// Product-page copy (features / pricing / use-cases / developers). Kept here as
// typed constants so it stays testable and consistent, and so the closed-loop
// language guard in the content tests covers every public sentence. Same rules
// as the landing copy: no payout/cash-out/redeem/withdraw, no "card networks"
// framing — coin flows in, never out.

/** Developer portal — labs onboarding, sandbox keys, key issuance. */
export const LABS_URL = "https://labs.ggcoin.ai";

/**
 * The four capability cards on /features. Reuses the {@link Pillar} shape so it
 * renders through the same card markup as the landing pillars.
 */
export const FEATURE_PILLARS: readonly Pillar[] = [
  {
    tag: "Agents",
    title: "Spend-capped sub-wallets over MCP",
    body: "Every agent gets its own sub-wallet, scoped by an MCP token bound to that wallet alone. Hard spend caps and velocity checks are enforced server-side, so a leaked token can never drain the master.",
  },
  {
    tag: "Approvals",
    title: "One-tap approval, like 3D Secure",
    body: "Small spends clear inside the limits you set. Anything above your threshold pauses and pushes to your phone, where one biometric tap releases it — strong customer authentication and near-zero chargebacks.",
  },
  {
    tag: "Closed-loop",
    title: "A virtual-goods coin",
    body: "Fiat in, coin out. GG-Coin is a closed-loop currency spent inside integrated platforms — a virtual good in the Robux and V-Bucks lineage. Coin only ever flows one way: in.",
  },
  {
    tag: "Developers",
    title: "Built on a real ledger",
    body: "A developer-first SDK, an append-only double-entry ledger that keeps every balance honest, HMAC-signed webhooks, and mandatory 2FA — the primitives a payment integration is supposed to ship with.",
  },
];

/** A two-column narrative block (copy + a supporting card of points). */
export interface NarrativeBlock {
  eyebrow: string;
  title: string;
  body: string;
  aside: string;
  points: readonly string[];
}

/** The alternating narrative blocks on /features (agent wallets, then approvals). */
export const FEATURE_BLOCKS: readonly NarrativeBlock[] = [
  {
    eyebrow: "Agent wallets",
    title: "Money an agent can spend — inside your limits",
    body: "An agent charges its own sub-wallet to buy what it needs — API credits, an in-app item, a service — from any platform that runs on GG-Coin. The MCP token it holds is bound to that one sub-wallet, so its reach is exactly what you granted and nothing more.",
    aside: "How the wallet is scoped",
    points: [
      "Sub-wallet-scoped MCP tokens — a compromise leaks one wallet, not the master",
      "Hard spend caps and velocity checks enforced server-side, never on the client",
      "Integer-exact amounts in the smallest unit — no floats, no rounding drift",
    ],
  },
  {
    eyebrow: "Approvals",
    title: "You stay in the loop on every real spend",
    body: "Set the threshold that feels right. Below it, the agent transacts on its own; above it, the charge waits for you. A push lands on your phone, you review the payee and amount, and one biometric tap releases it — or you decline.",
    aside: "How approval works",
    points: [
      "Push-to-approve over WebSocket, with APNs and FCM on mobile",
      "Biometric confirmation — strong customer authentication by default",
      "The agent only ever moves money you have authorized",
    ],
  },
];

/** A pricing tier. `price` is a structural label, never a fabricated amount. */
export interface PricingTier {
  name: string;
  price: string;
  tagline: string;
  features: readonly string[];
  cta: { label: string; href: string };
  featured?: boolean;
}

/**
 * Pricing STRUCTURE, not final numbers — billing is still being finalized (see
 * {@link PRICING_NOTE}). No dollar amounts anywhere; labels only.
 */
export const PRICING_TIERS: readonly PricingTier[] = [
  {
    name: "Sandbox",
    price: "Free",
    tagline: "Build and test the whole flow end to end.",
    features: [
      "Full-featured sandbox keys",
      "Every SDK and the MCP agent layer",
      "Double-entry ledger and signed webhooks",
      "Community support",
    ],
    cta: { label: "Start building", href: LABS_URL },
  },
  {
    name: "Production",
    price: "Usage-based",
    tagline: "Go live on a pay-as-you-grow rail.",
    features: [
      "Live keys and production wallets",
      "Agent sub-wallets and one-tap approvals",
      "HMAC-signed webhooks with delivery logs",
      "Mandatory 2FA on every account",
      "Email support",
    ],
    cta: { label: "Get production keys", href: LABS_URL },
    featured: true,
  },
  {
    name: "Scale",
    price: "Talk to us",
    tagline: "For high-volume platforms and studios.",
    features: [
      "Volume pricing and dedicated limits",
      "Priority support and onboarding help",
      "Reconciliation and audit tooling",
      "Multi-region readiness",
    ],
    cta: { label: "Contact us", href: "/support" },
  },
];

/** Honesty note shown on /pricing — pricing is not finalized. */
export const PRICING_NOTE =
  "Billing is still being finalized. The tiers below describe how pricing will be structured, not final prices — start free in the sandbox today, and we confirm production terms with you before anything is billed.";

/** A market segment on /use-cases: a short pitch plus a typical integration. */
export interface UseCase {
  eyebrow: string;
  title: string;
  pitch: string;
  integration: string;
  steps: readonly string[];
}

/** The three economies on /use-cases (agents first, per the positioning). */
export const USE_CASES: readonly UseCase[] = [
  {
    eyebrow: "AI agents",
    title: "Money your agents can spend",
    pitch:
      "Give autonomous agents a spend-capped wallet and a one-tap approval loop. They buy API credits, tools, and services across platforms on GG-Coin, and you stay in control of every real spend.",
    integration: "Issue a sub-wallet per agent, hand it an MCP token, and set a threshold.",
    steps: [
      "Create a sub-wallet and mint an MCP token bound to it",
      "Set the approval threshold and spend caps",
      "The agent posts charges; above-limit ones wait for your tap",
    ],
  },
  {
    eyebrow: "Gaming",
    title: "A coin and a wallet for your game",
    pitch:
      "Players buy coin once and spend it on in-game goods across your titles. One integration gives you a real wallet, a hardened ledger, and a payout-free economy — live in an afternoon, in any country.",
    integration: "Drop in an SDK, credit coin on purchase, and charge it for items and passes.",
    steps: [
      "Credit coin to a player wallet when a purchase confirms",
      "Post integer-exact charges for items, passes, and unlocks",
      "Reconcile against HMAC-signed webhooks",
    ],
  },
  {
    eyebrow: "Creator economy",
    title: "A spending currency for your community",
    pitch:
      "Give your community a coin to spend on unlocks, digital gifts, and memberships — virtual goods delivered inside your platform, backed by the same double-entry ledger and signed webhooks as everything else on GG-Coin.",
    integration: "Credit coin at checkout, then charge it for unlocks, gifts, and subscriptions.",
    steps: [
      "Credit coin at checkout as a virtual-goods purchase",
      "Charge it for unlocks, digital gifts, and memberships",
      "Track every entry in the append-only ledger",
    ],
  },
];

/**
 * Core API concepts on /developers. Reuses the {@link Pillar} shape so it
 * renders through the landing card markup.
 */
export const API_CONCEPTS: readonly Pillar[] = [
  {
    tag: "Auth",
    title: "API keys, sandbox first",
    body: "Grab a key in labs and initialize the client. Sandbox keys mirror production, so you can build and test the entire flow before you go live.",
  },
  {
    tag: "Money",
    title: "Integer-exact amounts",
    body: "Every amount is an integer in the coin's smallest unit — no floats, no decimals at rest, no rounding drift between your service and the ledger.",
  },
  {
    tag: "Ledger",
    title: "Double-entry, append-only",
    body: "Balances are derived from an append-only double-entry ledger. Nothing is edited in place; corrections are new entries that net to zero, so history stays auditable.",
  },
  {
    tag: "Webhooks",
    title: "HMAC-signed events",
    body: "Every webhook is signed with a timestamp and nonce and retried with backoff. Verify the signature and you can trust the event — no polling, no spoofing.",
  },
];

/**
 * Strings rendered inside the hero's animated approval-flow vignette (#266) —
 * an over-limit agent charge pausing for a one-tap phone approval, then landing
 * on the ledger as a double entry. Kept here so the closed-loop language guard
 * covers the scene like every other public sentence.
 */
export const HERO_VIGNETTE = {
  wallet: "agent-7 · sub-wallet",
  cap: "cap 500 GG / day",
  charge: { memo: "platform-y : api-credits", amount: "500 GG" },
  status: { waiting: "over limit — awaiting approval", approved: "approved · one tap" },
  ledger: { debit: "debit agent-7", credit: "credit platform-y", amount: "500" },
  push: { app: "GG-Coin · approval", body: "agent-7 wants to pay 500 GG to platform-y" },
  /** Assistive-tech description of the decorative animated scene. */
  description:
    "An agent's 500 GG charge pauses over its spend limit, a push notification lands on the owner's phone, one biometric tap approves it, and the ledger records the double entry.",
} as const;

/**
 * The ecosystem band under the hero (#267): where GG-Coin runs — the SDK
 * languages (rendered from {@link SDKS}) and the platforms they serve. Honest
 * pre-launch framing: "built for", never "used by"; text wordmark chips only,
 * no third-party logo art (trademark care).
 */
export const ECOSYSTEM = {
  eyebrow: "Built for the places your users already are",
  sdkLabel: "SDKs",
  platformLabel: "Platforms",
  platforms: ["FiveM", "Garry's Mod", "Minecraft", "Unity", "Web & backend", "MCP agents"],
  cta: { label: "Explore the SDKs", href: "/developers" },
} as const;

/** A trust-band credential tile: one product-truth guarantee, linked to docs. */
export interface TrustTile {
  title: string;
  body: string;
  href: string;
}

/**
 * The trust band (#268): the closed-loop analog of an enterprise security
 * section. Product truths only — the money-handling guarantees the rail
 * actually ships with. No uptime/SLA/adoption numbers, no compliance badges
 * we do not hold.
 */
export const TRUST = {
  title: "Built like money infrastructure",
  lede: "No adoption theater — the guarantees a payment integration is supposed to ship with.",
  tiles: [
    {
      title: "Append-only double-entry ledger",
      body: "Balances are derived, never edited in place. Corrections are new entries that net to zero, so history stays auditable.",
      href: `${DOCS_URL}/#concepts`,
    },
    {
      title: "Mandatory 2FA",
      body: "TOTP on every account, owner and operator alike. No opt-out.",
      href: `${DOCS_URL}/#concepts`,
    },
    {
      title: "HMAC-signed webhooks",
      body: "Timestamp + nonce on every event, retried with backoff. Verify the signature, trust the event.",
      href: `${DOCS_URL}/#concepts`,
    },
    {
      title: "Server-side spend caps",
      body: "Hard caps and velocity checks enforced on the server — never on the client.",
      href: `${DOCS_URL}/#concepts`,
    },
    {
      title: "Sub-wallet-scoped MCP tokens",
      body: "Each agent token is bound to one sub-wallet. A compromise leaks a wallet, never the master.",
      href: `${DOCS_URL}/#agents`,
    },
  ] satisfies readonly TrustTile[],
} as const;

/** One mock-UI row inside a lifecycle step card (#269). */
export interface StepUiRow {
  left: string;
  right: string;
  /** Visual tone of the right cell: credit/confirm (up), token chip, net total. */
  tone?: "up" | "chip" | "net";
}

/** One step of the landing lifecycle framework (#269). */
export interface LifecycleStep {
  label: string;
  title: string;
  body: string;
  rows: readonly StepUiRow[];
}

/**
 * The four-step "how it works" framework on the landing page (#269): the
 * wallet lifecycle in the order it happens — Fund → Delegate → Approve →
 * Audit. Absorbs the former Agents narrative (its copy lives in steps 2–3).
 * Each step pairs short copy with a stylized mock-UI card, so the numbering
 * encodes a real sequence, not decoration.
 */
export const HOW_IT_WORKS = {
  title: "One wallet, four moves",
  lede: "The whole lifecycle — from funding to an auditable ledger — in the order it happens.",
  steps: [
    {
      label: "01 · Fund",
      title: "Buy coin into your master wallet",
      body: "Fiat in, coin out. Integer-exact amounts on a real ledger from the first entry.",
      rows: [
        { left: "Master wallet", right: "12,500 GG" },
        { left: "checkout purchase", right: "+2,000 GG", tone: "up" },
      ],
    },
    {
      label: "02 · Delegate",
      title: "Give each agent a capped sub-wallet",
      body: "Its MCP token is bound to that wallet alone — a leaked token can never drain the master. Caps and velocity checks are enforced server-side.",
      rows: [
        { left: "agent-7", right: "cap 500 GG / day" },
        { left: "mcp_tok_•••7f2", right: "scoped", tone: "chip" },
      ],
    },
    {
      label: "03 · Approve",
      title: "Over the threshold, you decide",
      body: "Small spends clear on their own inside the limits you set. Bigger ones pause and push to your phone — one biometric tap releases them, or you decline.",
      rows: [
        { left: "platform-y : api-credits", right: "500 GG" },
        { left: "one-tap biometric", right: "approved", tone: "up" },
      ],
    },
    {
      label: "04 · Audit",
      title: "Every move on the ledger",
      body: "Append-only, double-entry. Nothing is edited in place — corrections net to zero, so history stays honest.",
      rows: [
        { left: "debit agent-7", right: "500" },
        { left: "credit platform-y", right: "500" },
        { left: "net", right: "0", tone: "net" },
      ],
    },
  ] satisfies readonly LifecycleStep[],
} as const;

/**
 * Conversion architecture (#270): the mid-page ask on the landing page and the
 * closing band rendered on the landing + all four product pages. Dual
 * audience: builders go to labs, wallet users go to the app.
 */
export const CONVERSION = {
  mid: {
    line: "Give your first agent a wallet in an afternoon.",
    cta: { label: "Start building", href: LABS_URL },
  },
  closing: {
    title: "Put a wallet in your agent's hands.",
    lede: "Sandbox keys mirror production — build the whole flow before you go live.",
    primary: { label: "Start building", href: LABS_URL },
    secondary: { label: "Open your wallet", href: WALLET_URL },
    alt: {
      lead: "Running a game or creator platform?",
      label: "See use cases",
      href: "/use-cases",
    },
  },
} as const;

/** One resources card on the landing page (#271). */
export interface ResourceCard {
  pill: string;
  title: string;
  body: string;
  label: string;
  href: string;
}

/**
 * The resources section (#271): awareness-stage content Glean closes with —
 * the thinking behind the coin and the fastest paths into it, all pointing at
 * existing docs content (no new destinations invented).
 */
export const RESOURCES = {
  title: "Go deeper",
  lede: "The thinking behind the coin, and the fastest paths into it.",
  cards: [
    {
      pill: "Thesis",
      title: "Why a closed-loop coin, and why now",
      body: "Gaming, AI, and the agent economy on one rail — the core concepts behind the product.",
      label: "Read the concepts",
      href: `${DOCS_URL}/#concepts`,
    },
    {
      pill: "Agents",
      title: "The agent guide",
      body: "Give an agent a wallet over MCP: sub-wallets, caps, and the approval loop.",
      label: "Read the guide",
      href: `${DOCS_URL}/#agents`,
    },
    {
      pill: "Quickstart",
      title: "SDK quickstarts",
      body: "From API key to first charge, in seven languages.",
      label: "Pick a language",
      href: `${DOCS_URL}/#quickstart`,
    },
  ] satisfies readonly ResourceCard[],
} as const;

/**
 * The landing FAQ (#271), rendered as an accordion and emitted as FAQPage
 * JSON-LD. Product/positioning questions only — user-support questions live in
 * SUPPORT_FAQ on /support. Every sentence sits under the closed-loop language
 * guard, so answers must explain the one-way flow without the banned words.
 */
export const LANDING_FAQ: readonly FaqEntry[] = [
  {
    question: "What is a closed-loop currency?",
    answer:
      "Fiat goes in, coin comes out, and the coin is spent inside integrated platforms. It only ever flows one way: in. That keeps GG-Coin a virtual good — in the Robux and V-Bucks lineage — not a money service.",
  },
  {
    question: "Is GG-Coin a crypto token?",
    answer:
      "No. There is no chain, no speculation, no exchange listing. GG-Coin is a virtual-goods currency on an append-only double-entry ledger, with mandatory 2FA on every account.",
  },
  {
    question: "How do agent approvals work?",
    answer:
      "Each agent charges its own spend-capped sub-wallet. Below your threshold it transacts on its own; above it, the charge pauses and pushes to your phone, where one biometric tap releases it — or you decline.",
  },
  {
    question: "Which platforms and SDKs are supported?",
    answer:
      "SDKs for JavaScript, Lua, Java, Python, C#, PHP, and Go — covering FiveM, Garry's Mod, Minecraft, Unity, and web or backend stacks — plus a native MCP layer for AI agents.",
  },
  {
    question: "How do I start building?",
    answer:
      "Grab a sandbox key at labs.ggcoin.ai. Sandbox mirrors production, so you can build and test the entire flow — wallets, charges, approvals, and webhooks — before you go live.",
  },
];

/** One link inside a nav dropdown group (#289): label + one-line description. */
export interface NavItem {
  label: string;
  href: string;
  desc: string;
}

/** A top-nav dropdown group (#289). */
export interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

/**
 * The top-nav dropdown groups (#289) — Glean-style rich items surfacing the
 * site's real content (docs chapters, labs onboarding), no invented pages.
 * Internal hrefs must appear in the prerender list (guarded by test); strings
 * sit under the closed-loop language guard like all public copy.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Product",
    items: [
      {
        label: "Features",
        href: "/features",
        desc: "Agent wallets, one-tap approvals, the closed-loop coin",
      },
      { label: "Pricing", href: "/pricing", desc: "Free sandbox, usage-based production" },
      {
        label: "Use cases",
        href: "/use-cases",
        desc: "AI agents, game servers, creator platforms",
      },
      { label: "How it works", href: "/#how", desc: "Fund → Delegate → Approve → Audit" },
    ],
  },
  {
    label: "Developers",
    items: [
      { label: "Developer overview", href: "/developers", desc: "The API concepts and every SDK" },
      { label: "Docs", href: DOCS_URL, desc: "Concepts, quickstart, and the full reference" },
      {
        label: "Quickstart",
        href: `${DOCS_URL}/#quickstart`,
        desc: "Zero to a posted transaction",
      },
      {
        label: "Agent guide",
        href: `${DOCS_URL}/#agents`,
        desc: "Give an agent a wallet over MCP",
      },
      { label: "API reference", href: `${DOCS_URL}/#api`, desc: "The most-used endpoints" },
      { label: "Get an API key", href: LABS_URL, desc: "Sandbox keys mirror production" },
    ],
  },
];
